import { useCallback, useState } from "react";
import { Button, Spinner, Link, Checkbox, toast } from "@heroui/react";
import { Upload as UploadIcon, AlertCircle, LucideInfo, Search } from "lucide-react";
import { DropZone, useDropZonePickerContext } from "@heroui-pro/react";
import { TemplatePtAPI } from "@repo/api";
import { useDecodeQuota, useInvalidateTrace } from "./use-trace";

type FileStatus = "idle" | "uploading" | "completed" | "error";
type DropZoneFileStatus = "uploading" | "complete" | "failed";

interface UploadFile {
  id: string;
  name: string;
  size: number;
  status: FileStatus;
  progress: number;
  file: File;
  error?: string;
  file_key?: string;
}

type FileFormatColor = "blue" | "gray" | "green" | "orange" | "purple" | "red";

const ACCEPTED_EXTENSIONS = [".psd", ".png", ".jpg", ".jpeg"] as const;
// accept 里若只有 image/* 类型，移动端浏览器 / WebView 会直接拉起相册而非文件选择器，
// 且相册里选不到 PSD。补充非 image 的 PSD MIME（多数安卓文件管理器把 PSD 识别为 octet-stream），
// 让系统弹出通用文件选择器；实际格式仍由 isValidFileFormat 按扩展名校验。
const ACCEPT_ATTR = [
  ...ACCEPTED_EXTENSIONS,
  "image/png",
  "image/jpeg",
  "image/vnd.adobe.photoshop",
  "application/x-photoshop",
  "application/photoshop",
  "application/psd",
  "application/octet-stream",
].join(",");
const MAX_FILE_SIZE = 300 * 1024 * 1024;

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getExtension(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.slice(dot + 1) : "";
}

function getFormatColor(ext: string): FileFormatColor {
  const map: Record<string, FileFormatColor> = {
    jpeg: "blue",
    jpg: "blue",
    png: "green",
    psd: "purple",
  };
  return map[ext.toLowerCase()] ?? "gray";
}

function toDropZoneStatus(status: FileStatus): DropZoneFileStatus | undefined {
  switch (status) {
    case "uploading":
      return "uploading";
    case "completed":
      return "complete";
    case "error":
      return "failed";
    default:
      return undefined;
  }
}

function ReplaceFileLink() {
  const { openFilePicker } = useDropZonePickerContext();

  return (
    <Link
      onPress={openFilePicker}
      className="shrink-0 cursor-pointer py-1 text-sm text-gray-500 transition-colors duration-200 hover:text-primary-600"
    >
      更换文件
    </Link>
  );
}

function isValidFileFormat(selected: File): boolean {
  const fileName = selected.name.toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => fileName.endsWith(ext));
}

export type TemplateTracePanelProps = {
  /** 上传到 COS，返回 fileKey */
  uploadFile: (file: File, onProgress: (percent: number) => void) => Promise<string>;
  isAuthenticated: boolean;
  onLoginRequired: () => void;
  onSubmitted?: () => void;
  className?: string;
};

export function TemplateTracePanel({
  uploadFile,
  isAuthenticated,
  onLoginRequired,
  onSubmitted,
  className,
}: TemplateTracePanelProps) {
  const [file, setFile] = useState<UploadFile | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);

  const isUploadDisabled = isUploading || !isAuthenticated || file?.status === "uploading";

  // 剩余配额走缓存；提交成功后与解码历史一起失效
  const { data: quota = { send_num: 0, decode_num: 0 } } = useDecodeQuota(isAuthenticated);
  const invalidateTrace = useInvalidateTrace();

  const handleFileSelect = useCallback(
    (selectedFile: File) => {
      if (isUploading || (file && file.status === "uploading")) {
        toast.danger("正在上传中，无法选择新文件");
        return;
      }

      if (!isValidFileFormat(selectedFile)) {
        toast.danger("仅支持 PSD、PNG、JPG 格式文件");
        return;
      }

      if (selectedFile.size > MAX_FILE_SIZE) {
        toast.danger("文件大小不能超过300MB");
        return;
      }

      setFile({
        id: Date.now().toString(),
        name: selectedFile.name,
        size: selectedFile.size,
        status: "idle",
        progress: 0,
        file: selectedFile,
      });
    },
    [isUploading, file],
  );

  const handleInputSelect = useCallback(
    (fileList: FileList) => {
      if (fileList[0]) {
        handleFileSelect(fileList[0]);
      }
    },
    [handleFileSelect],
  );

  const handleDrop = useCallback(
    async (e: { items: Array<{ kind: string; getFile?: () => Promise<File> }> }) => {
      if (isUploading || (file && file.status === "uploading")) {
        toast.danger("正在上传中，无法选择新文件");
        return;
      }

      for (const item of e.items) {
        if (item.kind === "file" && item.getFile) {
          const dropped = await item.getFile();
          handleFileSelect(dropped);
          break;
        }
      }
    },
    [file, handleFileSelect, isUploading],
  );

  const handleRemoveFile = () => {
    if (isUploading || (file && file.status === "uploading")) {
      toast.danger("文件正在上传中，无法移除");
      return;
    }
    setFile(null);
  };

  const updateProgress = useCallback((progress: number) => {
    setFile((prev) => {
      if (!prev) return null;
      return { ...prev, progress: Math.max(prev.progress, progress) };
    });
  }, []);

  const handleUpload = async () => {
    if (!file) {
      toast.danger("请先选择要上传的文件");
      return;
    }

    setIsUploading(true);
    setFile((prev) => (prev ? { ...prev, status: "uploading", progress: 0 } : null));

    try {
      const fileKey = await uploadFile(file.file, updateProgress);
      setFile((prev) =>
        prev ? { ...prev, status: "completed", progress: 100, file_key: fileKey } : null,
      );

      const submitResponse = await TemplatePtAPI.decodePsdTemplate(fileKey, file.file.name);

      if (!submitResponse.data) {
        throw new Error(submitResponse.msg || "提交失败");
      }

      const { error } = submitResponse.data;

      if (error) {
        toast.danger(error);
        setFile((prev) =>
          prev
            ? {
                ...prev,
                status: "error",
                error,
              }
            : null,
        );
        return;
      }

      toast.success("上传成功，稍后可在下方记录中查看查询结果");
      setFile(null);
      setAgreeTerms(false);
      invalidateTrace();
      onSubmitted?.();
    } catch (error: unknown) {
      const err = error as { msg?: string; message?: string };
      const errorMessage = err?.msg || err?.message || "上传失败，请重试";
      toast.danger(errorMessage);
      setFile((prev) =>
        prev
          ? {
              ...prev,
              status: "error",
              error: errorMessage,
            }
          : null,
      );
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className={className}>
      <div className="mb-8 text-center">
        <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-linear-to-br from-blue-500 to-indigo-600">
          <Search className="text-white" size={32} />
        </div>
        <h1 className="mb-2 text-2xl font-bold text-gray-800 sm:text-3xl">标记提取</h1>
        <p className="text-gray-500">上传您的模板源文件来识别文件来源</p>
      </div>

      {isAuthenticated && (
        <div className="mb-6 flex items-center justify-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-linear-to-r from-blue-50 to-indigo-50 px-4 py-2">
            <LucideInfo size={16} className="text-blue-500" />
            <span className="text-sm text-gray-600">
              今日剩余查询次数：
              <span
                className={`font-semibold ${quota.decode_num > 0 ? "text-blue-500" : "text-red-500"}`}
              >
                {quota.decode_num}
              </span>
            </span>
          </div>
        </div>
      )}

      {!isAuthenticated && (
        <div className="mb-6 flex flex-row items-center gap-2 rounded-xl border border-red-200 bg-red-50 p-4">
          <AlertCircle size={18} className="shrink-0 text-red-500" />
          <span className="text-sm text-red-600">
            标记提取功能需登录才可使用，请
            <Link
              onPress={onLoginRequired}
              className="cursor-pointer text-blue-500 hover:underline"
            >
              先登录
            </Link>
            。
          </span>
        </div>
      )}

      <div className="mb-6">
        <DropZone className="w-full">
          {!file && (
            <DropZone.Area isDisabled={isUploadDisabled} onDrop={handleDrop as never}>
              <DropZone.Icon />
              <DropZone.Label>点击或拖拽上传文件</DropZone.Label>
              <DropZone.Description>支持 PSD、PNG、JPG 格式，最大 300MB</DropZone.Description>
              <DropZone.Trigger isDisabled={isUploadDisabled}>选择文件</DropZone.Trigger>
            </DropZone.Area>
          )}

          <DropZone.Input
            accept={ACCEPT_ATTR}
            disabled={isUploadDisabled}
            onSelect={handleInputSelect}
          />

          {file && (
            <DropZone.FileList>
              <DropZone.FileItem status={toDropZoneStatus(file.status)}>
                <DropZone.FileFormatIcon
                  color={getFormatColor(getExtension(file.name))}
                  format={getExtension(file.name).toUpperCase() || "FILE"}
                />
                <DropZone.FileInfo>
                  <DropZone.FileName>{file.name}</DropZone.FileName>
                  <DropZone.FileMeta>
                    {formatFileSize(file.size)}
                    {file.status === "idle" && " | 待上传"}
                    {file.status === "uploading" && ` | 上传中 ${file.progress}%`}
                    {file.status === "completed" && (
                      <span className="text-success"> | 上传完成</span>
                    )}
                    {file.status === "error" && (
                      <span className="text-danger"> | {file.error || "上传失败"}</span>
                    )}
                  </DropZone.FileMeta>
                  {file.status === "uploading" && (
                    <DropZone.FileProgress value={file.progress}>
                      <DropZone.FileProgressTrack>
                        <DropZone.FileProgressFill />
                      </DropZone.FileProgressTrack>
                    </DropZone.FileProgress>
                  )}
                </DropZone.FileInfo>
                {file.status === "idle" && (
                  <div className="flex shrink-0 items-center gap-1">
                    <ReplaceFileLink />
                    <DropZone.FileRemoveTrigger
                      aria-label={`移除 ${file.name}`}
                      onPress={handleRemoveFile}
                    />
                  </div>
                )}
              </DropZone.FileItem>
            </DropZone.FileList>
          )}
        </DropZone>
      </div>

      <div className="mb-6 rounded-xl bg-gray-50 p-4">
        <div className="flex items-start gap-2">
          <AlertCircle size={18} className="mt-0.5 shrink-0 text-amber-500" />
          <div className="text-sm text-gray-600">
            <p className="mb-2 font-medium text-gray-700">使用说明</p>
            <ul className="list-inside list-disc space-y-1 text-gray-500">
              <li className="text-red-500">
                鉴定功能仅允许发货人本人查询，如果您不是该模板的发货人，您将得不到任何查询结果。
              </li>
              <li>支持 .psd 格式与图片格式的文件，一次仅允许上传一个文件进行查询。</li>
              <li>每帐号每日查询上限为5次，查询完成（包括空结果）后会扣除一次查询次数。</li>
              <li>请尽量使用原图作为查询材料，经过压缩、涂改、修改的文件可能无法识别。</li>
              <li>我们不对查询的文件做任何留存，再次查询视为重新查询。</li>
              <li>数字标记并不是一定能够百分百提取成功，结果仅供参考。</li>
            </ul>
          </div>
        </div>
        <Checkbox
          className="mt-2"
          isSelected={agreeTerms}
          onChange={setAgreeTerms}
          isDisabled={!file || isUploading || file?.status === "uploading"}
        >
          <Checkbox.Content>
            <Checkbox.Control>
              <Checkbox.Indicator />
            </Checkbox.Control>
            我已阅读并同意上述使用规则
          </Checkbox.Content>
        </Checkbox>
      </div>

      <div className="flex justify-center">
        <Button
          className="rounded-full bg-linear-to-r from-blue-500 to-indigo-600 px-8 py-2 text-white transition-all hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
          onPress={handleUpload}
          isDisabled={!file || isUploading || file?.status === "uploading" || !agreeTerms}
          isPending={isUploading}
        >
          {({ isPending }) => (
            <>
              {isPending ? <Spinner size="sm" color="current" /> : <UploadIcon size={18} />}
              {isPending ? "上传中..." : "提交"}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

export default TemplateTracePanel;
